import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { ArenaDisponivel, SessaoAberta } from '@arena/shared/schemas';
import { authApi } from '../api/auth.js';
import { ErroDeApi, ErroDeRede, tokens } from '../api/cliente.js';
import { Icone } from '../ui/Icone.js';
import { IlustracaoDePraia } from '../ui/IlustracaoDePraia.jsx';
import { Botao, Campo } from '../ui/primitivos.jsx';
import { TemaToggle } from '../ui/TemaToggle.jsx';
import { siglaDe } from '../ui/sigla.js';
import { useSessao } from './SessaoContext.jsx';

/**
 * `#login`, agora com senha real.
 *
 * O protótipo tinha três botões que entravam direto. Aqui o login é
 * uma CONVERSA de até três turnos, porque em multi-tenant ele não
 * devolve "logado" — devolve a próxima pergunta:
 *
 *   senha ok, 2FA ligado         → o código do autenticador
 *   senha ok, duas memberships   → em qual arena entrar
 *   senha ok, uma membership     → o par de tokens
 *
 * O que liga um turno ao outro é o `desafio`, nunca a senha de novo.
 *
 * Há uma quarta etapa, fora da conversa: "esqueci a senha". Ela existe
 * porque o produto não envia e-mail nem mensagem — quem esqueceu pede um
 * código de uso único ao Dono, que o gera na tela de Acessos, e troca a
 * senha aqui com e-mail + código. Ver `redefinicao-de-senha.service.ts`.
 *
 * Ver: Arena_backend/Docs/plano/02-identidade-sessao.md
 */

type Etapa =
  | { nome: 'credencial' }
  | { nome: 'totp'; desafio: string }
  | { nome: 'arena'; desafio: string; arenas: readonly ArenaDisponivel[] }
  | { nome: 'esqueci' };

export function Login() {
  const navegar = useNavigate();
  const local = useLocation();
  const { recarregar } = useSessao();
  // Para onde a pessoa ia quando caiu aqui (`RotaProtegida` guarda).
  // Sem destino, `/` escolhe a primeira tela que o perfil pode ver.
  const destino = (local.state as { de?: string } | null)?.de ?? '/';

  const [etapa, setEtapa] = useState<Etapa>({ nome: 'credencial' });
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [trocada, setTrocada] = useState(false);
  const [erro, setErro] = useState<string | undefined>();
  const [enviando, setEnviando] = useState(false);

  async function concluir(sessao: SessaoAberta) {
    tokens.gravar(sessao);
    await recarregar();
    navegar(destino, { replace: true });
  }

  async function tentar(acao: () => Promise<void>) {
    setErro(undefined);
    setEnviando(true);
    try {
      await acao();
    } catch (e) {
      // A mensagem vem do servidor de propósito: ela é genérica por
      // decisão dele ("Credenciais inválidas."), para não revelar quais
      // e-mails existem. Inventar um texto melhor aqui desfaria isso.
      if (e instanceof ErroDeRede) setErro('Não consegui falar com o servidor.');
      else if (e instanceof ErroDeApi) setErro(e.message);
      else setErro('Não consegui entrar. Tente de novo.');
    } finally {
      setEnviando(false);
    }
  }

  const { titulo, subtitulo } = TEXTOS_DA_ETAPA[etapa.nome];

  return (
    <div className="login">
      <section className="login-lado">
        <div className="login-topo">
          <span>Sand Sports · gestão de arenas</span>
          <TemaToggle />
        </div>

        <div className="login-centro">
          <div className="login-box">
            <img className="logo" src="/brand/logo-com-nome.webp" alt="Sand Sports" />
            <h1>{titulo}</h1>
            {subtitulo && <p>{subtitulo}</p>}

            {trocada && etapa.nome === 'credencial' && (
              <div className="ok" role="status" style={{ marginTop: 18 }}>
                Senha trocada. Entre com ela — as sessões antigas foram encerradas.
              </div>
            )}

            {etapa.nome === 'credencial' && (
              <form
                className="form"
                style={{ marginTop: 26, textAlign: 'left' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void tentar(async () => {
                    const resposta = await authApi.entrar({ email, senha });
                    if (resposta.etapa === 'pronto') await concluir(resposta);
                    else if (resposta.etapa === 'totp')
                      setEtapa({ nome: 'totp', desafio: resposta.desafio });
                    else
                      setEtapa({
                        nome: 'arena',
                        desafio: resposta.desafio,
                        arenas: resposta.arenas,
                      });
                  });
                }}
              >
                <Campo rotulo="E-mail">
                  <input
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Campo>
                <Campo rotulo="Senha">
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                  />
                </Campo>
                {erro && <div className="erro">{erro}</div>}
                <Botao type="submit" disabled={enviando}>
                  {enviando ? 'Entrando…' : 'Entrar'}
                </Botao>
              </form>
            )}

            {etapa.nome === 'totp' && (
              <form
                className="form"
                style={{ marginTop: 26, textAlign: 'left' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void tentar(async () => {
                    const resposta = await authApi.comTotp(etapa.desafio, codigo);
                    if (resposta.etapa === 'pronto') await concluir(resposta);
                    else if (resposta.etapa === 'arena')
                      setEtapa({
                        nome: 'arena',
                        desafio: resposta.desafio,
                        arenas: resposta.arenas,
                      });
                  });
                }}
              >
                <Campo
                  rotulo="Código do autenticador"
                  ajuda="Seis dígitos. Um código de recuperação também vale."
                >
                  <input
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    autoFocus
                    required
                    value={codigo}
                    onChange={(e) => setCodigo(e.target.value)}
                  />
                </Campo>
                {erro && <div className="erro">{erro}</div>}
                <Botao type="submit" disabled={enviando}>
                  {enviando ? 'Conferindo…' : 'Confirmar'}
                </Botao>
              </form>
            )}

            {etapa.nome === 'esqueci' && (
              <form
                className="form"
                style={{ marginTop: 26, textAlign: 'left' }}
                onSubmit={(e) => {
                  e.preventDefault();
                  void tentar(async () => {
                    await authApi.redefinirSenha({ email, codigo, novaSenha });
                    // A senha nova não loga ninguém: a pessoa entra com ela,
                    // e é assim que ela confirma que digitou o que queria.
                    setTrocada(true);
                    setCodigo('');
                    setNovaSenha('');
                    setSenha('');
                    setEtapa({ nome: 'credencial' });
                  });
                }}
              >
                <p className="sub" style={{ marginBottom: 4 }}>
                  Peça um <b>código de uso único</b> ao Dono da arena — ele gera na tela de Acessos.
                  Com o código em mãos, defina a senha nova aqui.
                </p>
                <Campo rotulo="E-mail">
                  <input
                    type="email"
                    autoComplete="username"
                    autoFocus
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Campo>
                <Campo rotulo="Código" ajuda='Oito caracteres, como "A1B2-C3D4".'>
                  <input
                    required
                    autoComplete="one-time-code"
                    placeholder="A1B2-C3D4"
                    value={codigo}
                    style={{ letterSpacing: 2, textTransform: 'uppercase' }}
                    onChange={(e) => setCodigo(e.target.value)}
                  />
                </Campo>
                <Campo rotulo="Senha nova" ajuda="Ao menos 10 caracteres.">
                  <input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={10}
                    value={novaSenha}
                    onChange={(e) => setNovaSenha(e.target.value)}
                  />
                </Campo>
                {erro && <div className="erro">{erro}</div>}
                <Botao type="submit" disabled={enviando}>
                  {enviando ? 'Trocando…' : 'Trocar a senha'}
                </Botao>
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setErro(undefined);
                    setEtapa({ nome: 'credencial' });
                  }}
                >
                  Voltar para o login
                </button>
              </form>
            )}

            {etapa.nome === 'arena' && (
              <>
                <div className="roles">
                  {etapa.arenas.map((arena) => (
                    <button
                      key={arena.arenaId}
                      className="role"
                      disabled={enviando}
                      onClick={() =>
                        void tentar(async () => {
                          await concluir(await authApi.comArena(etapa.desafio, arena.arenaId));
                        })
                      }
                    >
                      <span className="chip">{siglaDe(arena.nome)}</span>
                      <span>
                        <b>{arena.nome}</b>
                        <small>{arena.perfilNome}</small>
                      </span>
                      <Icone nome="go" className="ic go" />
                    </button>
                  ))}
                </div>
                {erro && <div className="erro">{erro}</div>}
              </>
            )}

            {etapa.nome === 'credencial' && (
              <p className="hint">
                Acesso para as equipes das arenas que usam o Sand Sports.{' '}
                <button
                  type="button"
                  className="link"
                  onClick={() => {
                    setErro(undefined);
                    setTrocada(false);
                    setEtapa({ nome: 'esqueci' });
                  }}
                >
                  Esqueci a senha
                </button>
              </p>
            )}
          </div>
        </div>

        <div className="login-rodape">
          <span>© {new Date().getFullYear()} Sand Sports</span>
          <span>Futevôlei · Vôlei · Beach tennis · Funcional</span>
        </div>
      </section>

      <aside className="login-arte" aria-hidden="true">
        <div>
          <span className="rotulo">PARA ARENAS DE AREIA</span>
          <h2>Agenda, alunos e caixa da arena no mesmo lugar.</h2>
          <div className="modalidades">
            <span>Futevôlei</span>
            <span>Vôlei de praia</span>
            <span>Beach tennis</span>
            <span>Funcional</span>
          </div>
        </div>
        <IlustracaoDePraia />
      </aside>
    </div>
  );
}

/**
 * O título de cada etapa da conversa. A etapa `esqueci` não tem subtítulo:
 * o formulário já abre com a explicação de como pedir o código.
 */
const TEXTOS_DA_ETAPA: Record<Etapa['nome'], { titulo: string; subtitulo?: string }> = {
  credencial: {
    titulo: 'Entre na sua conta',
    subtitulo: 'Use o e-mail que a sua arena cadastrou para você.',
  },
  totp: { titulo: 'Confirme que é você', subtitulo: 'Digite o código do app autenticador.' },
  arena: { titulo: 'Escolha a arena', subtitulo: 'Em qual arena você quer entrar?' },
  esqueci: { titulo: 'Redefinir a senha' },
};
